<template>
  <div class="language-switcher" ref="rootRef">
    <button
      ref="btnRef"
      class="language-btn"
      @click="toggleLanguageMenu"
      @keydown.down.prevent="openAndFocusFirst()"
      @keydown.enter.prevent="toggleLanguageMenu"
      :aria-expanded="showLanguageMenu ? 'true' : 'false'"
      aria-haspopup="menu"
      :aria-controls="menuId"
      type="button"
    >
      <i class="fas fa-language" aria-hidden="true"></i>
      <span class="current-language d-none d-md-inline">{{ currentLanguage }}</span>
    </button>

    <div
      v-show="showLanguageMenu"
      class="language-menu"
      :id="menuId"
      role="menu"
      @keydown.esc.stop.prevent="closeMenu"
      @keydown.up.prevent="focusPrev()"
      @keydown.down.prevent="focusNext()"
      @keydown.enter.prevent="activateFocused()"
      @keydown.space.prevent="activateFocused()"
    >
      <button
        v-for="(lang, idx) in availableLanguages"
        :key="lang.code"
        ref="itemRefs"
        class="language-item"
        role="menuitemradio"
        :aria-checked="locale === lang.code ? 'true' : 'false'"
        @click="switchLanguage(lang.code)"
        :class="{ active: locale === lang.code }"
        tabindex="-1"
        type="button"
        @mouseenter="focusedIndex = idx"
      >
        <span class="dot" :class="{ on: locale === lang.code }"></span>
        <span class="label">{{ lang.name }}</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";

const { locale } = useI18n();

const showLanguageMenu = ref(false);
const btnRef = ref(null);
const rootRef = ref(null);
const itemRefs = ref([]);
const focusedIndex = ref(0);
const menuId = `lang-menu-${Math.random().toString(36).slice(2, 8)}`;

const availableLanguages = [
  { code: "en", name: "English" },
  { code: "ar", name: "العربية" },
];

const currentLanguage = computed(() => {
  const found = availableLanguages.find((l) => l.code === locale.value);
  return found ? found.name : "English";
});

const nextTickFocusCurrent = () => {
  requestAnimationFrame(() => {
    const el = itemRefs.value[focusedIndex.value];
    el?.focus();
  });
};

const toggleLanguageMenu = () => {
  showLanguageMenu.value = !showLanguageMenu.value;

  if (showLanguageMenu.value) {
    const idx = availableLanguages.findIndex((l) => l.code === locale.value);
    focusedIndex.value = Math.max(0, idx);
    nextTickFocusCurrent();
  }
};

const openAndFocusFirst = () => {
  showLanguageMenu.value = true;
  focusedIndex.value = 0;
  nextTickFocusCurrent();
};

const closeMenu = () => {
  showLanguageMenu.value = false;
  btnRef.value?.focus();
};

const switchLanguage = (lang) => {
  locale.value = lang;
  localStorage.setItem("locale", lang);
  showLanguageMenu.value = false;
  btnRef.value?.focus();
};

const focusNext = () => {
  focusedIndex.value = (focusedIndex.value + 1) % availableLanguages.length;
  nextTickFocusCurrent();
};

const focusPrev = () => {
  focusedIndex.value =
    (focusedIndex.value - 1 + availableLanguages.length) % availableLanguages.length;
  nextTickFocusCurrent();
};

const activateFocused = () => {
  const lang = availableLanguages[focusedIndex.value]?.code;
  if (lang) switchLanguage(lang);
};

// Close on outside click (pointerdown works better on mobile)
const handlePointerDownOutside = (e) => {
  if (!showLanguageMenu.value) return;
  if (rootRef.value && !rootRef.value.contains(e.target)) {
    showLanguageMenu.value = false;
  }
};

onMounted(() => {
  document.addEventListener("pointerdown", handlePointerDownOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("pointerdown", handlePointerDownOutside);
});
</script>

<style scoped>
/* Wrapper */
.language-switcher {
  position: relative;
  display: inline-flex;
  align-items: center;
}

/* Button */
.language-btn {
  background: transparent;
  border: 0;
  cursor: pointer;

  color: #3c4858;
  border-radius: 999px;
  padding: 0.45rem 0.6rem;

  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  transition: background-color 0.2s ease, color 0.2s ease;
}

.language-btn i {
  font-size: 1.25rem;
  line-height: 1;
}

.language-btn:hover {
  color: #2c91ae;
  background: rgba(44, 145, 174, 0.08);
}

.current-language {
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1;
}

/* Menu (desktop default) */
.language-menu {
  position: absolute;
  top: calc(100% + 10px);
  inset-inline-end: 0;
  inset-inline-start: auto;

  width: 200px;
  max-width: 90vw;

  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.14);
  border-radius: 12px;
  overflow: hidden;

  z-index: 1200;
}

/* Mobile: fit screen perfectly */
@media (max-width: 991.98px) {
  .language-menu {
    position: fixed;
    top: 64px;
    inset-inline-end: 12px;
    inset-inline-start: auto;

    width: min(92vw, 240px);
    max-height: calc(100vh - 80px);
    overflow-y: auto;
  }
}

/* Items */
.language-item {
  width: 100%;
  border: 0;
  background: transparent;
  cursor: pointer;

  display: flex;
  align-items: center;
  gap: 0.6rem;

  padding: 0.65rem 1rem;
  text-align: start;

  color: #333;
  font-size: 0.95rem;

  transition: background-color 0.15s ease, color 0.15s ease;
}

.language-item:hover,
.language-item:focus {
  background: #f8f9fa;
  outline: none;
}

.language-item.active {
  color: #2c91ae;
  font-weight: 700;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  border: 2px solid rgba(44, 145, 174, 0.35);
  flex: 0 0 auto;
}

.dot.on {
  border-color: #2c91ae;
  background: #2c91ae;
}

.label {
  flex: 1 1 auto;
  min-width: 0;
}
</style>
